import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { AppConfig, APP_CONFIG } from '../app-config.module';

@Injectable()
export class Insights {
    private angularPlugin = new AngularPlugin();
    private appInsights = new ApplicationInsights({
        config: {
            instrumentationKey: this.appConfig.insightsInstrumentationKey,
            extensions: [this.angularPlugin],
            extensionConfig: {
                [this.angularPlugin.identifier]: {
                    router: this.router,
                    errorServices: [new ErrorHandler()],
                },
            },
        },
    });

    // constructor(private router: Router, private appConfig: AppConfig) {  // Get ... NullInjectorError: NullInjectorError: No provider for AppConfig!
    constructor(private router: Router,
        @Inject(APP_CONFIG) private appConfig: AppConfig
        ) {
        this.appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => this.redactSensitiveTelemetry(envelope));
        this.appInsights.loadAppInsights();

        // Removed: instrumentation key logging. SECURITY-AUDIT-2026-07.md (M-9).
    }

    // expose methods that can be used in components and services
    trackEvent(name: string): void {
        this.appInsights.trackEvent({ name });
    }

    trackTrace(message: string): void {
        this.appInsights.trackTrace({ message });
    }

    private redactSensitiveTelemetry(envelope: ITelemetryItem): boolean {
        const sensitive = /[?&](password|access_token)=/i;
        const baseData: any = envelope.data && (envelope.data as any).baseData
            ? (envelope.data as any).baseData
            : (envelope as any).baseData;
        const urlish = [
            envelope.name,
            baseData && baseData.name,
            baseData && baseData.url,
            baseData && baseData.uri,
            baseData && baseData.data,
            baseData && baseData.target,
            baseData && baseData.refUri,
        ].filter((v) => typeof v === 'string') as string[];

        const isAjax = envelope.baseType === 'RemoteDependencyData'
            || (baseData && (baseData.type === 'Ajax' || baseData.baseType === 'RemoteDependencyData'));

        // Drop ajax/dependency telemetry whose URL still carries secrets.
        if (isAjax && urlish.some((v) => sensitive.test(v))) {
            this.redactEnvelopeStrings(envelope);
            return false;
        }

        this.redactEnvelopeStrings(envelope);
        return true;
    }

    private redactSensitiveQuery(value: string): string {
        if (!value || typeof value !== 'string') {
            return value;
        }
        return value.replace(/([?&])(password|access_token)=([^&#]*)/gi, '$1$2=REDACTED');
    }

    private redactEnvelopeStrings(node: any): void {
        if (!node || typeof node !== 'object') {
            return;
        }
        for (const key of Object.keys(node)) {
            const val = node[key];
            if (typeof val === 'string') {
                node[key] = this.redactSensitiveQuery(val);
            } else if (val && typeof val === 'object') {
                this.redactEnvelopeStrings(val);
            }
        }
    }
}
